import { useState } from 'react'

import { setPostShowSessionState } from '@utils/localStateAccess'
import usePostStatsContext from '@contexts/postStatsContext'
import useSnackBarContext from '@contexts/snackBarContext'
import { useNavigate, useParams } from 'react-router-dom'
import useDrawerContext from '@contexts/drawerContext'
import { useAuthContext } from '@contexts/authContext'
import useModalContext from '@contexts/modalContext'
import { postStateInterface } from '@hooks/useFeed'
import { sizeToPx } from '@utils/postDimensions'
import { isAvatarSrc } from '@utils/thumbnails'
import snakecaseKeys from 'snakecase-keys'
import useYesOrNo from '@hooks/useYesOrNo'
import axios from '@utils/webRequests'
import { size } from '@styles/theme'
import vibrate from '@utils/vibrate'
import styled from '@emotion/styled'

import { OtherPostBottomDrawerContents } from '@components/layout/bottom_drawers/OtherPostBottomDrawerContents'
import { OwnPostBottomDrawerContents } from '@components/layout/bottom_drawers/OwnPostBottomDrawerContents'
import { PostActionBarFullScreen } from './post_elements/PostActionBarFullScreen'
import { BaseFullScreenPost } from './post_elements/BaseFullScreenPost'
import useGlobalPopoverContext from '@contexts/globalPopoverContext'
import { PostRightPanel } from './post_elements/PostRightPanel'
import { PostLeftPanel } from './post_elements/PostLeftPanel'
import { ReplyLine } from './post_elements/ReplyLine'
import { postTypes } from '@utils/postDisplayParser'
import YesOrNo from '@elements/YesOrNo'

const Container = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  max-width: 100vw;
  padding: ${size(1.25)} ${size(0.5)} 0 ${size(1)};
  border-bottom: 0.5px solid rgba(47, 51, 54);
  // border-bottom: 0.75px solid rgba(47, 51, 54);
  font-size: 15px;
  // font-size: 15.25px;
  text-align: left;
  line-height: ${size(2.5)};
  // line-height: 22px;
`

const PostDisplay = ({ post, user, mainPost, reposter, reposterPost, beforeLikeHook, beforeUnlikeHook, beforeRepostHook, index, feedAccessor, newReplyAfterCreate, afterDelete, isFullScreen, containerStyles={} }) => {
  const { dimensions, displayType } = post
  const [isRepostOpen, openRepost, closeRepost] = useYesOrNo(`post-repost-dialog-${index}-${post?.uuid}`)
  const { registerPopover } = useGlobalPopoverContext()
  const [panelWidth, setPanelWidth] = useState(0)
  const { openBottomDrawer } = useDrawerContext()
  const { state: authState } = useAuthContext()
  const { addSnackBar } = useSnackBarContext()
  const { getStat } = usePostStatsContext()
  const { newPost } = useModalContext()
  const navigate = useNavigate()
  const params = useParams()
  const { media } = post
  const { likeCount, replyCount, repostCount, currentUserLiked } = getStat(post?.uuid) || post || {}
  const isAvatar = isAvatarSrc('medium', user?.socialMedia?.thumbnail, user?.defaultAvatar)
  const isReplyBottomWithTop = displayType === 'reply_bottom_display' && mainPost?.display?.postType === postTypes.REPLY_TOP_BOTTOM
  const isReplyBottomOnly = displayType === 'reply_bottom_display' && mainPost?.display?.postType === postTypes.REPLY_BOTTOM_ONLY
  const isReplyBottom = displayType === 'reply_bottom_display'
  const isReplyTop = displayType === 'reply_top_display'
  const isRepost = displayType === 'repost_display'
  const isBase = displayType === 'base_display'
  const isFullScreenActionBar = isFullScreen && (isBase || isRepost || isReplyBottom)
  const isFullScreenBase = isFullScreen && isBase

  // Navigates when clicking mentions and hashtags
  const handleBodyClick = (e) => {
    e.preventDefault()
    const href = e.target.getAttribute('href')
    // Only stop propagation if a link was clicked.
    if (href && href.slice(0,7) !== '/search') {
      // TODO: we want to display a message to the user when the hashtag click doesn't work.
      //       ideally it would be the little grey bottom message when we build that feature.
      e.stopPropagation()
      navigate(href)
    }
  }

  const handleContainerClick = (e) => {
    e.preventDefault()
    // short circuit if we're on the post show page and we click on the curent post
    if (!post?.uuid || (isFullScreen && post?.uuid === params?.id)) return;
    setStateForForwardNav()
    navigate(`/posts/${post.uuid}`, { state: { post, feedMeta: { index, feedAccessor } }})
  }

  const setStateForForwardNav = () => {
    // We can set the main post into Session storage under most circumstances
    // NOTE: This doesn't work when the display post id isn't the main post id. 
    //       (when a reply top was clicked, or when reposts are clicked)
    if (post?.uuid === mainPost?.uuid) {
      // NOTE: If the post type is REPLY_BOTTOM_ONLY, then it won't render the reply top correctly in the post show component until getInitialItems returns
      const sessionState = structuredClone(postStateInterface)
      const postToSave = isReplyBottomOnly ? copyAndChangeReplyBottomOnly(mainPost) : mainPost
      sessionState.posts.push(postToSave)
      setPostShowSessionState(mainPost?.uuid, sessionState)
    }
  }

  const copyAndChangeReplyBottomOnly = (post) => {
    const newPost = structuredClone(post)
    newPost.display.postType = postTypes.REPLY_TOP_BOTTOM
    newPost.isReplyBottomOnly = false
    return newPost
  }

  const likePost = (e) => {
    e.stopPropagation()
    beforeLikeHook(post)
    axios.post(`/api/posts/${post.uuid}/likes${isRepost ? `?repost=${reposterPost.uuid}` : ''}`)
      .catch(console.error)
  }

  const unlikePost = (e) => {
    e.stopPropagation()
    beforeUnlikeHook(post)
    axios.delete(`/api/posts/${post.uuid}/likes${isRepost ? `?repost=${mainPost?.uuid}` : ''}`)
      .catch(console.err)
  }

  const handleRepost = () => {
    beforeRepostHook(post)
    // THIS NEEDS TO BE REDONE. pass in the repost like we do with likes/unlikes and set it on the backend
    const data = snakecaseKeys({ postId: post.uuid, })
    axios.post(`/api/my/reposts${isRepost ? `?repost_id=${mainPost?.uuid}` : ''}`, data)
      .then(() => {
        addSnackBar({ message: 'Repost Sent' })
        closeRepost()
      })
  }

  const handleRepostSelect = (yesOrNo) => {
    if (yesOrNo) handleRepost()
    else closeRepost()
  }

  const handleThreeDotsClick = registerPopover.wrapper((e) => {
    e.stopPropagation()
    vibrate()
    if (mainPost?.user?.uuid === authState?.user?.uuid && mainPost?.uuid === post?.uuid) {
      openBottomDrawer(
        <OwnPostBottomDrawerContents
          user={authState?.user}
          post={mainPost}
          afterDelete={afterDelete}
        />
      )
    } else {
      openBottomDrawer(
        <OtherPostBottomDrawerContents
          otherUser={user}
          post={mainPost}
        />
      )
    }
  })

  const handleReplyClick = registerPopover.wrapper((e) => {
    e.stopPropagation()
    vibrate()
    newPost.open({ search: { reply_to: user?.handle, reply_id: post.uuid, main_post_id: mainPost?.uuid }}, {}, { afterCreate: newReplyAfterCreate })
  })

  const handleRepostClick = registerPopover.wrapper((e) => {
    e.stopPropagation()
    vibrate()
    openRepost()
  })

  const handleLikeClick = registerPopover.wrapper((e) => {
    e.stopPropagation()
    !currentUserLiked && vibrate()
    currentUserLiked ? unlikePost(e) : likePost(e)
  })

  const handleShareClick = registerPopover.wrapper((e) => {
    e.stopPropagation()
    vibrate()
    navigator?.clipboard?.writeText(`${process.env.REACT_APP_CLIENT_URL}/posts/${post.uuid}`)
  })

  const postHeight = () => {
    if (isFullScreenActionBar) {
      return sizeToPx(dimensions?.postTotal?.heightInSize - dimensions?.rightPanel?.actionBar?.height)
    } else {
      if (!dimensions?.postTotal?.heightInPx) console.log('POST DOES NOT HAVE TOTAL HEIGHT')
      return dimensions?.postTotal?.heightInPx || 'auto'
    }
  }

  return (
    <>
      {!isFullScreenBase &&
        <Container
          className={`post post-clickable ${post?.uuid ? `post_id-${post?.uuid}` : ''}`}
          onClick={handleContainerClick}
          style={{
            height: postHeight(),
            borderBottom: (isReplyTop || isFullScreenActionBar) && 'none',
            overflow: 'hidden',
            ...containerStyles,
          }}>
          <YesOrNo
            isOpen={isRepostOpen}
            setClosed={closeRepost}
            handleSelect={handleRepostSelect}
            title='Repost Post?'
          />
          <ReplyLine
            isVisible={isReplyTop || isReplyBottomWithTop}
            displayAbove={isReplyBottomWithTop}
          />
          <PostLeftPanel
            user={user}
            isAvatar={isAvatar}
            isRepost={isRepost}
          />
          <PostRightPanel
            post={post}
            user={user}
            media={media}
            setPanelWidth={setPanelWidth}
            panelWidth={panelWidth}
            isRepost={isRepost}
            isReplyTop={isReplyTop}
            isReplyBottom={isReplyBottom}
            isFullScreenActionBar={isFullScreenActionBar}
            reposter={reposter}
            isFullScreen={isFullScreen}
            clickHandlers={{
              handleReplyClick,
              handleLikeClick,
              handleBodyClick,
              handleRepostClick,
              handleShareClick,
              handleThreeDotsClick,
            }}
            stats={{
              likeCount,
              replyCount,
              repostCount,
              currentUserLiked,
            }}
          />
        </Container>
      }
      {isFullScreenBase &&
        <>
          <YesOrNo
            isOpen={isRepostOpen}
            setClosed={closeRepost}
            handleSelect={handleRepostSelect}
            title='Repost Post?'
          />
          <BaseFullScreenPost
            post={post}
            user={user}
            media={media}
            setPanelWidth={setPanelWidth}
            panelWidth={panelWidth}
            isRepost={isRepost}
            isReplyTop={isReplyTop}
            isReplyBottom={isReplyBottom}
            isFullScreenActionBar={isFullScreenActionBar}
            reposter={reposter}
            currentUserLiked={currentUserLiked}
            isFullScreen={isFullScreen}
            handleBodyClick={handleBodyClick}
            handleThreeDotsClick={handleThreeDotsClick}
          />
        </>
      }
      {isFullScreenActionBar &&
        <div style={{ width: '100vw' }}>
          <PostActionBarFullScreen
            post={post}
            clickHandlers={{
              handleReplyClick,
              handleLikeClick,
              handleBodyClick,
              handleRepostClick,
              handleShareClick,
              handleThreeDotsClick,
            }}
            stats={{
              likeCount,
              replyCount,
              repostCount,
              currentUserLiked,
            }}
          />
        </div>
      }
    </>
  )
}

export default PostDisplay